import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import axios from "axios";
import swal from "sweetalert";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Pagination,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
    },
    (error) => Promise.reject(error)
);

const ITEMS_PER_PAGE = 10;

function BankAdmin() {
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [openAdd, setOpenAdd] = useState(false);
    const [formData, setFormData] = useState({ name: "", short_name: "", code: "" });

    const fetchBanks = () => {
        setLoading(true);
        axios
            .get(`${process.env.REACT_APP_API_URL}/available-bank/list`)
            .then((res) => {
                setBanks(res.data?.data || []);
                setLoading(false);
            })
            .catch(() => {
                setBanks([]);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchBanks();
    }, []);

    const handleCreate = () => {
        if (!formData.name || !formData.short_name) {
            swal("Lỗi", "Vui lòng nhập đầy đủ thông tin!", "error");
            return;
        }
        axios
            .post(`${process.env.REACT_APP_API_URL}/available-bank/create`, formData)
            .then((res) => {
                swal("Thành công", res.data?.message || "Thêm ngân hàng thành công!", "success");
                setOpenAdd(false);
                setFormData({ name: "", short_name: "", code: "" });
                fetchBanks();
            })
            .catch((err) => {
                swal("Lỗi", err.response?.data?.message || "Có lỗi xảy ra!", "error");
            });
    };

    const handleDelete = (id, bankName) => {
        swal({
            title: "Xác nhận xoá?",
            text: `Bạn có chắc muốn xoá ngân hàng "${bankName}"?`,
            icon: "warning",
            buttons: ["Huỷ", "Xoá"],
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                axios
                    .delete(`${process.env.REACT_APP_API_URL}/available-bank/delete/${id}`)
                    .then(() => {
                        swal("Đã xoá!", "Xoá ngân hàng thành công.", "success");
                        fetchBanks();
                    })
                    .catch((err) => {
                        swal("Lỗi", err.response?.data?.message || "Có lỗi xảy ra!", "error");
                    });
            }
        });
    };

    const handleSeed = () => {
        swal({
            title: "Import danh sách mặc định?",
            text: "Sẽ thêm các ngân hàng chưa có vào danh sách. Ngân hàng đã tồn tại sẽ được bỏ qua.",
            icon: "info",
            buttons: ["Huỷ", "Import"],
        }).then((willSeed) => {
            if (willSeed) {
                axios
                    .post(`${process.env.REACT_APP_API_URL}/available-bank/seed`)
                    .then((res) => {
                        swal("Thành công", res.data?.message || "Import thành công!", "success");
                        fetchBanks();
                    })
                    .catch((err) => {
                        swal("Lỗi", err.response?.data?.message || "Có lỗi xảy ra!", "error");
                    });
            }
        });
    };

    const totalPages = Math.ceil(banks.length / ITEMS_PER_PAGE);
    const paginatedBanks = banks.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        py: 4,
                        px: 2,
                        minHeight: "100vh",
                        backgroundColor: "#f4f6f8",
                    }}
                >
                    <Container maxWidth="lg">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 3,
                            }}
                        >
                            <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 700 }}>
                                Quản lý ngân hàng khả dụng
                            </h2>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    onClick={() => setOpenAdd(true)}
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                >
                                    + Thêm mới
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={handleSeed}
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                >
                                    Danh sách ngân hàng khả dụng
                                </Button>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                backgroundColor: "#fff",
                                borderRadius: "12px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                overflow: "hidden",
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#1a1a2e" }}>
                                        <TableCell
                                            sx={{
                                                color: "#fff !important",
                                                fontWeight: "700 !important",
                                                fontSize: "14px",
                                                padding: "14px 16px",
                                            }}
                                        >
                                            Ngân hàng
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: "#fff !important",
                                                fontWeight: "700 !important",
                                                fontSize: "14px",
                                                padding: "14px 16px",
                                            }}
                                        >
                                            Tên rút gọn
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: "#fff !important",
                                                fontWeight: "700 !important",
                                                fontSize: "14px",
                                                padding: "14px 16px",
                                                textAlign: "center",
                                            }}
                                        >
                                            Tác vụ
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ textAlign: "center", py: 4, color: "#888" }}>
                                                Đang tải...
                                            </TableCell>
                                        </TableRow>
                                    ) : paginatedBanks.length > 0 ? (
                                        paginatedBanks.map((bank) => (
                                            <TableRow
                                                key={bank._id}
                                                sx={{
                                                    "&:hover": { backgroundColor: "#f8f9fa" },
                                                    borderBottom: "1px solid #eee",
                                                }}
                                            >
                                                <TableCell sx={{ padding: "12px 16px", fontSize: "13px" }}>
                                                    {bank.name}
                                                </TableCell>
                                                <TableCell sx={{ padding: "12px 16px", fontSize: "13px", fontWeight: 600 }}>
                                                    {bank.short_name}
                                                </TableCell>
                                                <TableCell sx={{ padding: "12px 16px", textAlign: "center" }}>
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleDelete(bank._id, bank.short_name)}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontWeight: 600,
                                                            minWidth: "60px",
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        Xoá
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ textAlign: "center", py: 4, color: "#888" }}>
                                                Chưa có ngân hàng nào. Nhấn "Danh sách ngân hàng khả dụng" để import danh sách mặc định.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>

                            {totalPages > 1 && (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(e, val) => setPage(val)}
                                        color="primary"
                                        size="small"
                                    />
                                </Box>
                            )}
                        </Box>
                    </Container>
                </Box>

                {/* Dialog thêm mới */}
                <Dialog
                    open={openAdd}
                    onClose={() => setOpenAdd(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: "12px" },
                    }}
                >
                    <DialogTitle
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontWeight: 700,
                            fontSize: "18px",
                            borderBottom: "1px solid #eee",
                            pb: 1,
                        }}
                    >
                        Thêm ngân hàng
                        <IconButton onClick={() => setOpenAdd(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: "20px !important" }}>
                        <TextField
                            label="Tên đầy đủ ngân hàng"
                            placeholder="VD: Ngân hàng TMCP Ngoại Thương Việt Nam"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={{ mb: 2 }}
                            size="small"
                        />
                        <TextField
                            label="Tên rút gọn"
                            placeholder="VD: Vietcombank"
                            fullWidth
                            value={formData.short_name}
                            onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                            sx={{ mb: 2 }}
                            size="small"
                        />
                        <TextField
                            label="Mã ngân hàng (tuỳ chọn)"
                            placeholder="VD: VCB"
                            fullWidth
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                            size="small"
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button
                            onClick={() => setOpenAdd(false)}
                            sx={{ textTransform: "none" }}
                        >
                            Huỷ
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleCreate}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                            Thêm
                        </Button>
                    </DialogActions>
                </Dialog>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default BankAdmin;
